import { useEffect, useRef } from "react";
import { HotelPaginationPropertiesType } from "./hotel-pagination.types";

const PAGE_SIZE = 20;

export const useLoadMore = (properties: HotelPaginationPropertiesType) => {
  const { loading, pages, setPages, totalCount } = properties;
  const sentinelReference = useRef<HTMLDivElement | null>(null);
  const lockReference = useRef(false);
  const timeoutReference = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sentinel = sentinelReference.current;
    if (!sentinel || loading) {
      return;
    }

    const maxPage = Math.ceil(totalCount / PAGE_SIZE);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentPage = pages.at(-1) || 1;
        const hasMorePage = maxPage > currentPage;

        if (!entry?.isIntersecting || !hasMorePage || lockReference.current) {
          return;
        }

        lockReference.current = true;
        timeoutReference.current = setTimeout(() => {
          setPages((state) => {
            const nextPage = currentPage + 1;
            if (state.includes(nextPage)) {
              return state;
            }

            return [...state, nextPage].sort(
              (firstPage, secondPage) => firstPage - secondPage
            );
          });

          lockReference.current = false;
        }, 300);
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (timeoutReference.current) {
        clearTimeout(timeoutReference.current);
      }
      lockReference.current = false;
    };
  }, [loading, pages, setPages, totalCount]);

  return { reference: sentinelReference };
};
